import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/path';
import { 
  HomeIcon, 
  UsersIcon, 
  DocumentTextIcon, 
  Cog8ToothIcon, 
  ChartBarIcon,
  CreditCardIcon,
  SparklesIcon,
  ArchiveBoxIcon,
  SunIcon,
  MoonIcon,
  BuildingStorefrontIcon,
  ChevronUpDownIcon,
  CheckBadgeIcon,
  QrCodeIcon,
  InboxIcon,
  LockClosedIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import CreateBusinessModal from './CreateBusinessModal';

const navigation = [
  { name: 'Dashboard', href: PATHS.DASHBOARD, icon: HomeIcon, permission: 'dashboard' },
  { name: 'Customers', href: PATHS.CUSTOMERS, icon: UsersIcon, permission: 'customers' },
  { name: 'Transactions', href: PATHS.TRANSACTIONS, icon: DocumentTextIcon, permission: 'transactions' },
  { name: 'Inventory', href: PATHS.PRODUCTS, icon: ArchiveBoxIcon, permission: 'inventory' },
  { name: 'Reports', href: PATHS.REPORTS, icon: ChartBarIcon, permission: 'reports', lockedFeature: 'exportReports' },
  { name: 'Payments', href: PATHS.PAYMENTS, icon: CreditCardIcon, permission: 'payments' },
  { name: 'Staff & Payroll', href: '/employees', icon: UsersIcon, permission: 'dashboard' },
  { name: 'Daily Attendance', href: '/attendance', icon: CheckBadgeIcon, permission: 'dashboard' },
  { name: 'QR Kiosk', href: '/scanner', icon: QrCodeIcon, permission: 'dashboard' },
  { name: 'Mailbox', href: '/mailbox', icon: InboxIcon, permission: 'dashboard', lockedFeature: 'mailbox' },
];

const bottomNavigation = [
  { name: 'Visit Website', href: PATHS.LANDING, icon: GlobeAltIcon, permission: 'dashboard' },
  { name: 'Manage Khatas', href: '/businesses', icon: BuildingStorefrontIcon, permission: 'settings' },
  { name: 'App Users', href: PATHS.STAFF, icon: UsersIcon, permission: 'staff' },
  { name: 'Billing & Invoices', href: PATHS.BILLING, icon: DocumentTextIcon, permission: 'settings' },
  { name: 'Settings', href: PATHS.SETTINGS, icon: Cog8ToothIcon, permission: 'settings' },
];

const LeftSidebar = ({ isMobileOpen, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, businesses, activeBusiness, switchBusiness } = useAuth();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  
  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectBusiness = (id) => {
    if (id === 'ADD_NEW') {
      const canAddMultiple = user?.subscription?.features?.multipleBusiness;
      if (!canAddMultiple && businesses.length >= 1) {
        window.dispatchEvent(new CustomEvent('showUpgradeModal', {
          detail: { message: `You have reached the limit of 1 khata on your current plan. Upgrade to Premium to manage multiple khatas.` }
        }));
        setIsDropdownOpen(false);
        return;
      }
      navigate('/businesses');
    } else {
      switchBusiness(id);
    }
    setIsDropdownOpen(false);
  };
  
  const effectiveRole = (user?.role === 'user' ? 'owner' : user?.role) || 'cashier';
  const isOwner = ['owner', 'admin', 'super-admin'].includes(effectiveRole);
  const permissions = user?.permissions || [];

  const hasPermission = (perm) => {
    if (isOwner) return true;
    return permissions.includes(perm);
  };

  const businessLogoUrl = activeBusiness?.assets?.logoUrl;

  // Helper to get logo for any business
  const getBusinessLogo = (b) => b?.assets?.logoUrl;

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={onClose}
        />
      )}

      <div className={`fixed inset-y-0 left-0 z-50 flex flex-col w-[270px] bg-white dark:bg-[#0b1120] border-r border-gray-100 dark:border-gray-800/60 transform transition-transform duration-300 lg:relative lg:translate-x-0 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      
      {/* Logo Area — Premium Header */}
      <div className="h-[72px] flex items-center px-5 border-b border-gray-100 dark:border-gray-800/60 transition-colors duration-300">
        <Link to={PATHS.DASHBOARD} className="flex items-center gap-3 group">
          {/* Animated Logo */}
          <div className="relative flex items-center justify-center w-10 h-10 bg-gradient-to-br from-[#dc343b] to-[#ff6b6b] rounded-xl shadow-lg shadow-red-500/20 group-hover:shadow-red-500/40 transition-all duration-300 group-hover:scale-105">
            <svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='currentColor' className="text-white drop-shadow-sm">
              <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
            </svg>
            {/* Shine effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-[17px] font-extrabold tracking-tight text-[#0f172a] dark:text-white uppercase leading-tight transition-colors duration-300">
              NetrootX
            </span>
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 tracking-widest uppercase">
              Business Suite
            </span>
          </div>
        </Link>
      </div>

      {/* Custom Business Switcher Dropdown */}
      {businesses && businesses.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800/60" ref={dropdownRef}>
          <div className="relative">
            {/* Trigger Button */}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-300 cursor-pointer ${
                isDropdownOpen 
                  ? 'bg-white dark:bg-gray-800 border-[#dc343b]/40 dark:border-red-500/30 shadow-md shadow-red-500/5 ring-2 ring-[#dc343b]/10 dark:ring-red-500/10' 
                  : 'bg-gradient-to-r from-gray-50 to-gray-100/80 dark:from-gray-800/80 dark:to-gray-800/40 border-gray-200/80 dark:border-gray-700/50 hover:border-[#dc343b]/30 dark:hover:border-red-500/20 hover:shadow-sm'
              }`}
            >
              {/* Business Logo or Fallback Avatar */}
              {businessLogoUrl ? (
                <img 
                  src={businessLogoUrl} 
                  alt={activeBusiness?.name} 
                  className="w-8 h-8 rounded-lg object-cover ring-2 ring-white dark:ring-gray-700 shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#dc343b] to-[#ff6b6b] flex items-center justify-center text-white text-[12px] font-extrabold shadow-sm flex-shrink-0 ring-2 ring-white dark:ring-gray-700">
                  {activeBusiness?.name?.charAt(0)?.toUpperCase() || 'K'}
                </div>
              )}
              <div className="flex-1 text-left min-w-0">
                <p className="text-[13px] font-bold text-gray-800 dark:text-white truncate">
                  {activeBusiness?.name || 'Select Business'}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium capitalize">
                  {activeBusiness?.type || 'business'}
                </p>
              </div>
              <ChevronUpDownIcon className={`h-4 w-4 flex-shrink-0 transition-all duration-300 ${
                isDropdownOpen ? 'text-[#dc343b] rotate-180' : 'text-gray-400 dark:text-gray-500'
              }`} />
            </button>

            {/* Dropdown Panel */}
            <div className={`absolute left-0 right-0 mt-2 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-700/60 shadow-xl shadow-black/8 dark:shadow-black/30 overflow-hidden z-50 transition-all duration-200 origin-top ${
              isDropdownOpen 
                ? 'opacity-100 scale-100 translate-y-0' 
                : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
            }`}>
              {/* Header */}
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Switch Business</p>
              </div>
              
              {/* Business List */}
              <div className="max-h-[200px] overflow-y-auto py-1 scrollbar-hide">
                {businesses.map((b) => {
                  const isActive = activeBusiness?._id === b._id;
                  const bLogo = getBusinessLogo(b);
                  return (
                    <button
                      key={b._id}
                      onClick={() => handleSelectBusiness(b._id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 transition-all duration-150 ${
                        isActive 
                          ? 'bg-red-50 dark:bg-red-950/20' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      {/* Logo */}
                      {bLogo ? (
                        <img src={bLogo} alt={b.name} className="w-7 h-7 rounded-lg object-cover ring-1 ring-gray-200 dark:ring-gray-700 flex-shrink-0" />
                      ) : (
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-extrabold flex-shrink-0 ${
                          isActive 
                            ? 'bg-gradient-to-br from-[#dc343b] to-[#ff6b6b] text-white' 
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                        }`}>
                          {b.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      {/* Name */}
                      <span className={`flex-1 text-left text-[13px] font-semibold truncate ${
                        isActive ? 'text-[#dc343b] dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {b.name}
                      </span>
                      {/* Check mark */}
                      {isActive && (
                        <svg className="w-4 h-4 text-[#dc343b] dark:text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Add New Khata */}
              {isOwner && (
                <div className="border-t border-gray-100 dark:border-gray-800 py-1">
                  <button
                    onClick={() => handleSelectBusiness('ADD_NEW')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-lg border-2 border-dashed border-[#dc343b]/40 dark:border-red-500/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-[#dc343b] dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                    </div>
                    <span className="text-[13px] font-bold text-[#dc343b] dark:text-red-400">Add New Khata</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6 scrollbar-hide">
        
        <div>
          <p className="px-3 text-[10px] font-bold tracking-[0.15em] text-gray-400/80 dark:text-gray-500 uppercase mb-3">Main Menu</p>
          <ul className="space-y-0.5">
            {navigation.filter(item => hasPermission(item.permission)).map((item) => {
              const isLocked = item.lockedFeature && user?.subscription?.features && !user?.subscription?.features[item.lockedFeature];

              const linkContent = (isActive) => (
                <>
                  <div className={`p-1 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#dc343b]/10 dark:bg-red-500/10' 
                      : 'bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-gray-700/50'
                  }`}>
                    <item.icon 
                      className={`h-[18px] w-[18px] shrink-0 transition-colors duration-200 ${
                        isActive ? 'text-[#dc343b] dark:text-red-400' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                      }`} 
                      aria-hidden="true" 
                    />
                  </div>
                  {item.name}
                  {isLocked && (
                    <div className="ml-auto flex items-center justify-center p-1 bg-gray-100 dark:bg-gray-800 rounded-md">
                      <LockClosedIcon className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  )}
                  {isActive && !isLocked && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#dc343b] dark:bg-red-400 shadow-sm shadow-red-500/50"></div>
                  )}
                </>
              );

              if (isLocked) {
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('showUpgradeModal', {
                          detail: { message: `Upgrade your plan to access ${item.name}.` }
                        }));
                      }}
                      className="w-full text-left group flex items-center gap-3 px-3 py-2.5 text-[13.5px] font-semibold rounded-xl transition-all duration-200 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white"
                    >
                      {linkContent(false)}
                    </button>
                  </li>
                );
              }

              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 text-[13.5px] font-semibold rounded-xl transition-all duration-200 ${
                        isActive 
                          ? 'bg-gradient-to-r from-red-50 to-red-50/50 dark:from-red-950/30 dark:to-red-900/10 text-[#dc343b] dark:text-red-400 shadow-sm shadow-red-500/5 border border-red-100/50 dark:border-red-800/20' 
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white hover:translate-x-0.5'
                      }`
                    }
                  >
                    {({ isActive }) => linkContent(isActive)}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="pt-2">
          <p className="px-3 text-[10px] font-bold tracking-[0.15em] text-gray-400/80 dark:text-gray-500 uppercase mb-3">Preferences</p>
          <ul className="space-y-0.5">
            {bottomNavigation.filter(item => hasPermission(item.permission)).map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  className="group flex items-center gap-3 px-3 py-2.5 text-[13.5px] font-semibold rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white hover:translate-x-0.5 transition-all duration-200"
                >
                  <div className="p-1 rounded-lg bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-gray-700/50 transition-all duration-200">
                    <item.icon className="h-[18px] w-[18px] shrink-0 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200" />
                  </div>
                  {item.name}
                </NavLink>
              </li>
            ))}
            {/* Theme Toggle */}
            <li>
              <button
                onClick={toggleTheme}
                className="w-full group flex items-center gap-3 px-3 py-2.5 text-[13.5px] font-semibold rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white hover:translate-x-0.5 transition-all duration-200"
              >
                <div className="p-1 rounded-lg bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-gray-700/50 transition-all duration-200">
                  {theme === 'dark' ? (
                    <SunIcon className="h-[18px] w-[18px] shrink-0 text-amber-400 group-hover:text-amber-500 transition-colors duration-200" />
                  ) : (
                    <MoonIcon className="h-[18px] w-[18px] shrink-0 text-gray-400 group-hover:text-indigo-500 transition-colors duration-200" />
                  )}
                </div>
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* Upgrade Premium Card — Glass Effect */}
      <div className="p-3 mt-auto mb-3 mx-3">
        <Link to="/pricing" className="block">
        <div className="relative bg-gradient-to-br from-[#dc343b] via-[#e84c52] to-[#ff6b6b] dark:from-[#dc343b]/90 dark:via-[#c42a31] dark:to-[#a01f25] rounded-2xl p-4 overflow-hidden cursor-pointer group hover:shadow-xl hover:shadow-red-500/15 transition-all duration-300 hover:-translate-y-0.5">
          {/* Background decorative elements */}
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500"></div>
          <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full blur-lg"></div>
          <div className="absolute top-2 right-2 opacity-20 group-hover:opacity-40 group-hover:rotate-12 transition-all duration-500">
            <SparklesIcon className="w-10 h-10 text-white" />
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-md bg-white/20 flex items-center justify-center">
                <SparklesIcon className="w-3 h-3 text-white" />
              </div>
              <h4 className="text-white font-extrabold text-[13px] tracking-tight">Upgrade to Pro</h4>
            </div>
            <p className="text-white/70 text-[11px] mb-3 leading-relaxed max-w-[85%]">
              Unlock advanced reports, priority support & more.
            </p>
            <div className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg transition-all duration-200 group-hover:gap-2">
              Explore Plans
              <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </div>
          </div>
        </div>
        </Link>
      </div>

      {isCreateModalOpen && (
        <CreateBusinessModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </div>
    </>
  );
};

export default LeftSidebar;
